import React, { useCallback, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Alert, Button } from '@mui/material';

import OrderSearchForm, { MultiFilterState } from '~/components/Common/Forms/OrderSearchForm';
import Spinner from '~/components/Common/Spinner';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';
import useQuery from '~/hooks/useQuery';

import { getPurchases } from '~/services/OrdersService';

import { mustFilterOrders } from '~/utils/adminPermission';
import { addQuery, removeQuery } from '~/utils/url';

import PackagesPage from './PackagesPage';

const TYPE_ACCOMMODATION = 'accommodation';
const DEFAULT_SIZE_PER_PAGE = 10;
const SEARCH_BY_CUSTOMER_ID_SIZE_PER_PAGE = 50;

function BedbankPageContainer() {
  const { push } = useAppNavigation();
  const location = useLocation();
  const query = useQuery();
  const { user } = useCurrentUser();
  const brand = useSelector((state: App.State) => state.tenant.brand);

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [purchases, setPurchases] = useState(null);
  const [filterField, setFilterField] = useState(null);
  const [filterValue, setFilterValue] = useState(null);
  // bumped on every submit so repeating an identical search still refetches
  const [searchNonce, setSearchNonce] = useState(0);
  const [additionalSearchParams, setAdditionalSearchParams] = useState<MultiFilterState>(null);

  const [sizePerPage, setSizePerPage] = useState(DEFAULT_SIZE_PER_PAGE);

  const currentPage = parseInt(query.get('page')) || 1;
  const customerId = query.get('customer_id');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    setPurchases(null);

    if (mustFilterOrders(user)) {
      // we don't want these users to be able to see all results so
      // if there are no filters selected, just return empty
      if (!filterField || !filterValue) {
        setPurchases({
          result: [],
          total: 0,
        });
        setIsLoading(false);
        return;
      }
    }

    try {
      const fetchedPurchases = await getPurchases({
        page: currentPage,
        per_page: sizePerPage,
        brand,
        customer_id: customerId,
        filterBy: filterField,
        filterValue,
        additionalFilter: additionalSearchParams,
        item_type: TYPE_ACCOMMODATION,
      });

      if (!fetchedPurchases) {
        setHasError(true);
        return;
      }

      setPurchases(fetchedPurchases);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [user?.roles, filterField, filterValue, currentPage, sizePerPage, brand, customerId, additionalSearchParams]);

  const onPageChange = (page) => {
    const newLocation = addQuery(location, { page });
    push(newLocation);
  };

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [fetchData, user, searchNonce]);

  useEffect(() => {
    if (customerId) {
      setSizePerPage(SEARCH_BY_CUSTOMER_ID_SIZE_PER_PAGE);
    } else {
      setSizePerPage(DEFAULT_SIZE_PER_PAGE);
    }
  }, [customerId]);

  const searchQuery = (field, value) => {
    setFilterField(field);
    setFilterValue(value);
    setSearchNonce((nonce) => nonce + 1);

    const newLocation = removeQuery(location, 'customer_id', 'page');

    push(newLocation);
  };

  return (
    <>
      <OrderSearchForm
        searchQuery={searchQuery}
        setAdditionalParams={setAdditionalSearchParams}
        showOfferIdFilter
        showOfferTypeFilter
        bookingIdType="booking_numbers"
      />
      <>
        {isLoading && <Spinner size={48} />}

        {!isLoading && hasError && (
          <Alert
            severity="error"
            action={
              <Button variant="text" color="primary" onClick={fetchData}>
                Retry
              </Button>
            }
          >
            Unable to load orders. Please retry your search.
          </Alert>
        )}

        {!isLoading && !hasError && purchases && (
          <PackagesPage
            purchases={purchases.result}
            total={purchases.total}
            page={currentPage}
            sizePerPage={sizePerPage}
            onPageChange={onPageChange}
          />
        )}
      </>
    </>
  );
}

export default BedbankPageContainer;
